import type { Planet, TickCheckpointEventOf } from '@kaeliux/shared';
import { calculatePlanetTick, previewBuildingCancel, REFUND_RATES } from '@kaeliux/game-engine';
import { formatResourceText } from './resourceQuantities';

function formatLevelList(levels: number[]): string {
    if (levels.length <= 1) return levels.join('');
    return `${levels.slice(0, -1).join(', ')} and ${levels[levels.length - 1]}`;
}

/**
 * The planet's Metal, Minerals and Gas as of `now`: the store only moves
 * when the server ticks (up to a heartbeat ago), and a storage cap reached
 * since then is exactly what decides how much of a refund is credited.
 */
function withResourcesAt(planet: Planet, now: number, resourceMultiplier: number): Planet {
    const seconds = (now - planet.state.lastTickAt.getTime()) / 1_000;
    if (seconds <= 0) return planet;
    const projected = calculatePlanetTick(planet, seconds, resourceMultiplier, { now: new Date(now) });
    return {
        ...planet,
        resources: {
            ...planet.resources,
            metal: projected.resources.metal,
            minerals: projected.resources.minerals,
            gas: projected.resources.gas,
        },
    };
}

/**
 * Message (and storage warning) for the confirm dialog shown before
 * cancelling a queued building level: which levels the cascade removes and
 * the refund the planet will actually be credited. Shared by the zone list
 * and the surface popover so both say the same thing.
 */
export function cancelBuildDialogContent(
    planet: Planet,
    event: TickCheckpointEventOf<'buildingBuild'>,
    now: number,
    resourceMultiplier: number,
): { message: string; warning?: string } {
    let preview: ReturnType<typeof previewBuildingCancel>;
    try {
        preview = previewBuildingCancel(withResourcesAt(planet, now, resourceMultiplier), planet.tickCheckpoints ?? [], event.eventId, new Date(now));
    } catch {
        preview = { levels: [event.eventDetails.targetLevel], nominalRefund: {}, creditedRefund: {} };
    }
    const { levels, creditedRefund, nominalRefund } = preview;
    const scope = levels.length > 1
        ? `This cancels levels ${formatLevelList(levels)}, because the higher levels can't be built without level ${levels[0]}.`
        : `This cancels level ${levels[0]}.`;
    const lostToStorage = (['metal', 'minerals', 'gas'] as const).some((key) => (creditedRefund[key] ?? 0) < (nominalRefund[key] ?? 0));
    return {
        message: `${scope} You will be refunded ${Math.round(REFUND_RATES.building * 100)}% of the resources spent: ${formatResourceText(creditedRefund, 'nothing')}.`,
        warning: lostToStorage ? 'Storage is near capacity — part of this refund will be lost.' : undefined,
    };
}
